import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

// The one page chassis every console module renders in (decision
// deck-ia-console): title + colored subtitle + one-line help, a top-right
// context slot (app selector, status chip, primary actions), an optional
// stepper row, optional tabs, the body (modules usually lay out three
// columns with <ModuleFrame.Columns>), and a bottom "three verbs" strip.
// Dark theme + tokens from index.css; no module-specific styling here.

// Tab state lives in the URL (?tab=) so deep links and the sidebar redirects
// (`listen` → `data?tab=datasets`) land on the right pane.
export function useModuleTab(defaultId) {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || defaultId;
  // extra: { phase, id, … } — sub-state a tab wants in the URL (the Lab's
  // phase/record) so suggestions and copilot links can deep-link into it.
  const setTab = (id, extra = null) => {
    const next = new URLSearchParams(params);
    if (id === defaultId) next.delete('tab'); else next.set('tab', id);
    if (extra) for (const [k, v] of Object.entries(extra)) { if (v == null || v === '') next.delete(k); else next.set(k, String(v)); }
    setParams(next, { replace: true });
  };
  return [tab, setTab];
}

export default function ModuleFrame({
  title,
  subtitle,
  accent = 'var(--surf)',
  help,
  context = null,
  steps = null,      // [{ id, label, state: 'done'|'current'|'todo', to?, hint? }]
  lead = null,       // optional node above the tabs (the lifecycle rail)
  tabs = null,       // [{ id, label, count? }]
  tab = null,
  onTab = null,
  verbs = null,      // [{ label, hint, to?, onClick?, primary? }]
  note = null,       // one paragraph NoVA reads about this module's state
                     // (data-nova-note; the screen outline quotes it verbatim,
                     // ≤320 chars). Every module passes one — novaContract.test
                     // checks. Say what is on screen and which tools act on it.
  children,
}) {
  return (
    <div className="mf" data-nova-note={note || undefined}>
      <header className="mf-head">
        <div className="mf-head-text">
          <h1 className="mf-title">{title}</h1>
          {subtitle && <p className="mf-subtitle" style={{ '--mf-accent': accent }}>{subtitle}</p>}
          {help && <p className="mf-help">{help}</p>}
        </div>
        {context && <div className="mf-context">{context}</div>}
      </header>

      {steps && steps.length > 0 && (
        <ol className="mf-steps" aria-label="Steps">
          {steps.map((s, i) => {
            const inner = (
              <>
                <span className="mf-step-dot" aria-hidden="true">{s.state === 'done' ? '✓' : i + 1}</span>
                <span className="mf-step-label">{s.label}</span>
                {s.hint && <span className="mf-step-hint">{s.hint}</span>}
              </>
            );
            return (
              <li key={s.id} className={`mf-step is-${s.state || 'todo'}`}>
                {s.to ? <Link to={s.to} className="mf-step-link">{inner}</Link> : <span className="mf-step-link">{inner}</span>}
              </li>
            );
          })}
        </ol>
      )}

      {lead}

      {tabs && tabs.length > 0 && (
        <nav className="mf-tabs" aria-label="Sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`mf-tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => onTab && onTab(t.id)}
            >
              {t.label}
              {t.count !== undefined && t.count !== null && <span className="mf-tab-count">{t.count}</span>}
            </button>
          ))}
        </nav>
      )}

      <div className="mf-body">{children}</div>

      {verbs && verbs.length > 0 && (
        <footer className="mf-verbs">
          {verbs.map((v) => {
            const cls = `cs-btn ${v.primary ? 'cs-btn-primary' : 'cs-btn-outline'} cs-btn-sm`;
            return (
              <div key={v.label} className="mf-verb">
                {v.to ? (
                  <Link to={v.to} className={cls}>{v.label}</Link>
                ) : (
                  <button type="button" className={cls} onClick={v.onClick} disabled={v.disabled}>{v.label}</button>
                )}
                {v.hint && <span className="mf-verb-hint">{v.hint}</span>}
              </div>
            );
          })}
        </footer>
      )}
    </div>
  );
}

// Three-column body (collapses to one on narrow screens).
ModuleFrame.Columns = function Columns({ children, cols = 3 }) {
  return <div className={`mf-cols mf-cols-${cols}`}>{children}</div>;
};

// A titled panel inside a column.
ModuleFrame.Panel = function Panel({ title, meta, actions, children, className = '' }) {
  return (
    <section className={`mf-panel ${className}`}>
      {(title || actions) && (
        <div className="mf-panel-head">
          <div>
            {title && <h2 className="mf-panel-title">{title}</h2>}
            {meta && <span className="mf-panel-meta">{meta}</span>}
          </div>
          {actions && <div className="mf-panel-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
};

// Status chip for the context slot / tables.
export function Chip({ tone = 'muted', children, title }) {
  return <span className={`mf-chip is-${tone}`} title={title}>{children}</span>;
}

// Plain key/value rows.
export function KV({ rows }) {
  return (
    <dl className="mf-kv">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
